import { z } from "zod";

export const companyAddressSchema = z.object({
	street: z.string().max(255).optional(),
	city: z.string().max(100).optional(),
	state: z.string().max(100).optional(),
	postalCode: z.string().max(20).optional(),
	country: z.string().max(100).optional(),
});

export const createAddressSchema = z.object({
	type: z.enum(["billing", "shipping", "hq"]).default("shipping"),
	addressName: z.string().min(1).max(50),
	addressName2: z.string().max(100).optional(),
	addressName3: z.string().max(100).optional(),
	street: z.string().max(255).optional(),
	city: z.string().max(100).optional(),
	state: z.string().max(100).optional(),
	postalCode: z.string().max(20).optional(),
	country: z.string().max(100).optional(),
});

export const updateAddressSchema = createAddressSchema.omit({ type: true }).partial();

export const createLeadSchema = z.object({
	firstName: z.string().max(100).optional(),
	lastName: z.string().max(100).optional(),
	email: z.string().email().optional(),
	phone: z.string().max(50).optional(),
	companyName: z.string().min(1).max(255),
	tradeName: z.string().max(255).optional(),
	taxNumber: z.string().max(50).optional(),
	taxOffice: z.string().max(100).optional(),
	fax: z.string().max(50).optional(),
	website: z.string().max(255).optional(),
	industry: z.string().max(100).optional(),
	sapGroupCode: z.number().int(),
	currency: z.enum(["TRY", "USD", "EUR"]).optional(),
	title: z.string().max(100).optional(),
	source: z.string().max(100).optional(),
	campaign: z.string().max(100).optional(),
	notes: z.string().optional(),
	assignedTo: z.string().uuid().optional(),
	address: companyAddressSchema.optional(),
});

export const updateLeadSchema = createLeadSchema.partial().extend({
	email: z.union([z.string().email(), z.literal("")]).optional(),
	companyName: z.string().min(1).max(255).optional(),
});

export const convertLeadSchema = z.object({
	companyId: z.string().uuid().optional(),
	personId: z.string().uuid().optional(),
	legalName: z.string().max(255).optional(),
	tradeName: z.string().max(255).optional(),
	status: z.enum(["lead", "customer", "vendor", "inactive"]).optional(),
	sapSeries: z.number().int().optional(),
	sapGroupCode: z.number().int().optional(),
	currency: z.string().max(10).optional(),
	currencies: z.array(z.string().max(10)).optional(),
	taxNumber: z.string().max(50).optional(),
	taxOffice: z.string().max(100).optional(),
	email: z.string().email().max(255).optional(),
	phone: z.string().max(50).optional(),
	fax: z.string().max(50).optional(),
	website: z.string().max(255).optional(),
	industry: z.string().max(100).optional(),
	notes: z.string().optional(),
	ownerId: z.string().uuid().optional(),
	address: companyAddressSchema.optional(),
});

export const createOpportunitySchema = z.object({
	title: z.string().min(1).max(255),
	description: z.string().optional(),
	estimatedValue: z.coerce.number().nonnegative().optional(),
	currency: z.string().max(10).optional(),
	probability: z.coerce.number().min(0).max(100).default(0),
	expectedCloseDate: z.coerce.date().optional(),
	status: z.enum(["open", "won", "lost", "abandoned"]).optional(),
	companyId: z.string().uuid().optional(),
	primaryPersonId: z.string().uuid().optional(),
	pipelineId: z.string().uuid().optional(),
	stageId: z.string().uuid().optional(),
	assignedTo: z.string().uuid().optional(),
});

export const updateOpportunitySchema = createOpportunitySchema.partial();

export const changeStageSchema = z.object({
	stageId: z.string().uuid(),
	notes: z.string().optional(),
});

export const ACTIVITY_TYPES = [
	"call",
	"meeting",
	"email",
	"note",
	"task",
	"whatsapp",
	"sms",
	"visit",
	"service",
	"demo",
	"offer_presentation",
	"collection",
	"complaint",
	"event",
] as const;

export const ACTIVITY_DIRECTIONS = ["inbound", "outbound"] as const;

export const ACTIVITY_OUTCOMES = [
	"positive",
	"negative",
	"no_answer",
	"follow_up",
	"postponed",
] as const;

export const createActivitySchema = z.object({
	type: z.enum(ACTIVITY_TYPES),
	subject: z.string().min(1).max(255),
	content: z.string().optional(),
	dueDate: z.coerce.date().optional(),
	companyId: z.string().uuid().optional(),
	personId: z.string().uuid().optional(),
	opportunityId: z.string().uuid().optional(),
	assignedTo: z.string().uuid().optional(),
	isCompleted: z.boolean().optional(),
	completedDate: z.coerce.date().optional(),
	contactName: z.string().max(255).optional(),
	contactOrgName: z.string().max(255).optional(),
	contactPhone: z.string().max(50).optional(),
	contactEmail: z.string().max(255).optional(),
	location: z.string().max(255).optional(),
	direction: z.enum(ACTIVITY_DIRECTIONS).optional(),
	outcome: z.enum(ACTIVITY_OUTCOMES).optional(),
});

export const updateActivitySchema = createActivitySchema.partial();

export const offerLineSchema = z.object({
	lineNumber: z.coerce.number().int().nonnegative(),
	itemCode: z.string().max(50).optional(),
	itemDescription: z.string().max(500).optional(),
	displayDescription: z.string().max(500).optional(),
	lineDetail: z.string().optional(),
	quantity: z.coerce.number().positive(),
	unit: z.string().max(50).optional(),
	unitPrice: z.coerce.number().nonnegative(),
	givenUnitPrice: z.coerce.number().nonnegative().optional(),
	vatRate: z.coerce.number().min(0).max(100).optional(),
	vatGroup: z.string().max(20).optional(),
	discountPercent: z.coerce.number().min(-100000).max(100000).optional(),
	lineTotal: z.coerce.number().nonnegative().optional(),
	notes: z.string().optional(),
	factorySpecialNotes: z.string().optional(),
	generalNotes: z.string().optional(),
	alternativeGroup: z.coerce.number().int().nonnegative().optional(),
	alternativeCurrency: z.string().max(10).optional(),
	priceComponents: z
		.array(
			z.object({
				label: z.string().max(200),
				price: z.coerce.number(),
				separate: z.boolean().optional(),
			}),
		)
		.optional(),
	priceListModel: z.string().max(100).optional(),
	commitmentValue: z.string().max(50).optional(),
	commitmentUnit: z.enum(["day", "week", "month"]).optional(),
	stockItemId: z.string().uuid().optional(),
	isSection: z.boolean().default(false),
	sectionItems: z
		.array(
			z.object({
				name: z.string().max(1000),
				quantity: z.coerce.number().nonnegative(),
				unitPrice: z.coerce.number().nonnegative(),
			}),
		)
		.optional(),
	isFreeText: z.boolean().default(false),
	showInPdf: z.boolean().default(true),
	showPriceInPdf: z.boolean().default(true),
	showDiscountInPdf: z.boolean().default(false),
	sendDatasheetInEmail: z.boolean().default(true),
	isCustomDesign: z.coerce.boolean().default(false),
});

export const createOfferSchema = z.object({
	documentNumber: z.string().max(50).optional(),
	offerDate: z.coerce.date().optional(),
	validUntil: z.coerce.date().optional(),
	commitmentDate: z.coerce.date().optional(),
	commitmentValue: z.string().max(50).optional(),
	commitmentUnit: z.enum(["day", "week", "month"]).optional(),
	currency: z.string().max(10).optional(),
	exchangeRate: z.coerce.number().positive().optional(),
	paymentTerms: z.string().max(255).optional(),
	deliveryTerms: z.string().max(255).optional(),
	notes: z.string().optional(),
	internalNotes: z.string().optional(),
	pdfNotes: z.string().optional(),
	buyerExtraInfo: z.string().optional(),
	contractNo: z.string().max(100).optional(),
	contractDate: z.coerce.date().optional(),
	isTaxIncluded: z.boolean().default(false),
	showGtipInPdf: z.boolean().default(false),
	grossTotal: z.coerce.number().nonnegative().optional(),
	termsCommissioning: z.string().optional(),
	termsAssembly: z.string().optional(),
	termsVat: z.string().optional(),
	termsQuality: z.string().optional(),
	termsValidity: z.string().optional(),
	termsWarranty: z.string().optional(),
	termsPayment: z.string().optional(),
	termsDelivery: z.string().optional(),
	termsPaymentMethod: z.string().optional(),
	termsI18n: z.record(z.string(), z.record(z.string(), z.string())).optional(),
	offerOrderDeliveryType: z.string().max(50).optional(),
	companyId: z.string().uuid().optional(),
	companySapAccountId: z.string().uuid().nullable().optional(),
	leadId: z.string().uuid().optional(),
	contactPersonId: z.string().uuid().optional(),
	opportunityId: z.string().uuid().optional(),
	assignedTo: z.string().uuid().optional(),
	lines: z.array(offerLineSchema).min(1),
});

export const updateOfferSchema = createOfferSchema.partial().extend({
	lines: z.array(offerLineSchema).optional(),
});

export const sendOfferSchema = z.object({
	email: z.string().email(),
	subject: z.string().min(1).max(255),
	message: z.string().optional(),
});

export const orderLineSchema = z.object({
	lineNumber: z.coerce.number().int().nonnegative(),
	itemCode: z.string().max(50).optional(),
	itemDescription: z.string().max(500).optional(),
	displayDescription: z.string().max(500).optional(),
	lineDetail: z.string().optional(),
	quantity: z.coerce.number().positive(),
	unit: z.string().max(50).optional(),
	unitPrice: z.coerce.number().nonnegative(),
	vatRate: z.coerce.number().min(0).max(100).optional(),
	vatGroup: z.string().max(20).optional(),
	discountPercent: z.coerce.number().min(0).max(100).optional(),
	lineTotal: z.coerce.number().nonnegative().optional(),
	notes: z.string().optional(),
	atsDescription: z.string().optional(),
	tmsDescription: z.string().optional(),
	factorySpecialNotes: z.string().optional(),
	generalNotes: z.string().optional(),
	priceComponents: z
		.array(
			z.object({
				label: z.string().max(200),
				price: z.coerce.number(),
				separate: z.boolean().optional(),
			}),
		)
		.optional(),
	priceListModel: z.string().max(100).optional(),
	stockItemId: z.string().uuid().optional(),
	warehouseId: z.string().uuid().optional(),
	isCustomDesign: z.coerce.boolean().default(false),
});

export const createOrderSchema = z.object({
	documentNumber: z.string().max(50).optional(),
	orderDate: z.coerce.date().optional(),
	deliveryDate: z.coerce.date().optional(),
	currency: z.string().max(10).optional(),
	exchangeRate: z.coerce.number().positive().optional(),
	paymentTerms: z.string().trim().min(1).max(255),
	deliveryTerms: z.string().max(255).optional(),
	shippingAddress: z.string().max(254).optional(),
	shippingAddressId: z.string().uuid().optional(),
	notes: z.string().optional(),
	internalNotes: z.string().optional(),
	startupDescription: z.string().optional(),
	installationDescription: z.string().optional(),
	tmsDescription: z.string().optional(),
	atsDescription: z.string().optional(),
	factorySpecialNotes: z.string().optional(),
	generalNotes: z.string().optional(),
	companyId: z.string().uuid().optional(),
	contactPersonId: z.string().uuid().optional(),
	opportunityId: z.string().uuid().optional(),
	sourceOfferId: z.string().uuid().optional(),
	assignedTo: z.string().uuid().optional(),
	subtotal: z.coerce.number().nonnegative().optional(),
	vatTotal: z.coerce.number().nonnegative().optional(),
	total: z.coerce.number().nonnegative().optional(),
	paidAmount: z.coerce.number().nonnegative().optional(),
	status: z
		.enum([
			"draft",
			"confirmed",
			"partially_delivered",
			"delivered",
			"invoiced",
			"closed",
			"cancelled",
		])
		.optional(),
	paymentStatus: z.enum(["unpaid", "partially_paid", "paid", "overpaid"]).optional(),
	lines: z.array(orderLineSchema).min(1),
});

export const convertOfferToOrderSchema = z.object({
	alternativeGroup: z.coerce.number().int().nonnegative().optional(),
	/** Birden çok alternatif tek siparişe dönüştürülebilir; doluysa alternativeGroup yok sayılır. */
	alternativeGroups: z.array(z.coerce.number().int().nonnegative()).nonempty().optional(),
	deliveryDate: z.coerce.date(),
	paymentTerms: z.string().trim().min(1).max(255),
	shippingAddress: z.string().max(254).optional(),
	shippingAddressId: z.string().uuid().optional(),
	lineWarehouses: z.record(z.string().uuid(), z.string().uuid().nullable()).optional(),
});

export const updateOrderSchema = createOrderSchema.partial().extend({
	lines: z.array(orderLineSchema).optional(),
});

export const recordPaymentSchema = z.object({
	amount: z.coerce.number().positive(),
	currency: z.string().max(10).optional(),
	exchangeRate: z.coerce.number().positive().optional(),
	method: z.enum(["cash", "bank_transfer", "credit_card", "check", "letter_of_credit", "other"]),
	reference: z.string().max(255).optional(),
	notes: z.string().optional(),
	receivedAt: z.coerce.date().optional(),
});

export const createContractSchema = z.object({
	title: z.string().min(1).max(255),
	type: z.enum(["sales", "maintenance", "rental", "service"]).default("sales"),
	status: z.enum(["draft", "active", "expired", "terminated", "renewed"]).optional(),
	startDate: z.coerce.date().optional(),
	endDate: z.coerce.date().optional(),
	value: z.coerce.number().nonnegative().optional(),
	currency: z.string().max(10).optional(),
	autoRenewal: z.boolean().default(false),
	renewalNoticeDays: z.coerce.number().int().nonnegative().optional(),
	terms: z.string().optional(),
	notes: z.string().optional(),
	companyId: z.string().uuid().optional(),
	opportunityId: z.string().uuid().optional(),
	orderId: z.string().uuid().optional(),
	offerId: z.string().uuid().optional(),
	assignedTo: z.string().uuid().optional(),
	clauseTexts: z.record(z.string(), z.string()).optional(),
});

export const updateContractSchema = createContractSchema.partial();

export const createCompanySchema = z.object({
	legalName: z.string().min(1).max(255),
	tradeName: z.string().max(255).optional(),
	status: z.enum(["lead", "customer", "vendor", "inactive"]).default("lead"),
	sapSeries: z.number().int().optional(),
	sapGroupCode: z.number().int().optional(),
	currency: z.string().max(10).optional(),
	currencies: z.array(z.string().max(10)).min(1).optional(),
	taxNumber: z.string().max(50).optional(),
	taxOffice: z.string().max(100).optional(),
	email: z.string().email().max(255).optional(),
	phone: z.string().max(50).optional(),
	fax: z.string().max(50).optional(),
	website: z.string().max(255).optional(),
	industry: z.string().max(100).optional(),
	notes: z.string().optional(),
	ownerId: z.string().uuid().optional(),
	address: companyAddressSchema.optional(),
});

export const updateCompanySchema = z
	.object({
		legalName: z.string().min(1).max(255).optional(),
		tradeName: z.string().max(255).optional(),
		taxNumber: z.string().max(50).optional(),
		taxOffice: z.string().max(100).optional(),
		registrationNumber: z.string().max(100).optional(),
		industry: z.string().max(100).optional(),
		sapGroupCode: z.number().int().optional(),
		size: z.string().max(50).optional(),
		website: z.string().max(255).optional(),
		email: z.string().email().max(255).optional(),
		phone: z.string().max(50).optional(),
		fax: z.string().max(50).optional(),
		balance: z.coerce.number().optional(),
		currency: z.string().max(10).optional(),
		notes: z.string().optional(),
		status: z.enum(["lead", "customer", "vendor", "inactive"]).optional(),
		ownerId: z.string().uuid().optional(),
		address: companyAddressSchema.optional(),
	})
	.partial();
